// 얼굴 소품: 팩(K-뷰티, 라군 팩 3종), 오이, 코 풍선. 프로토타입 1171–1178, 1216–1272.
import * as THREE from 'three';
import type { LagoonMaskId } from '@capy/shared';
import { mergeGeo } from '../engine/geometry';

const CUKE_MATS = (() => {
  const c = document.createElement('canvas'); c.width = c.height = 128; const x = c.getContext('2d')!;
  x.fillStyle = '#2E6B2E'; x.fillRect(0, 0, 128, 128); x.fillStyle = '#B9E07A'; x.beginPath(); x.arc(64, 64, 56, 0, Math.PI * 2); x.fill(); x.fillStyle = '#DDF2A8'; x.beginPath(); x.arc(64, 64, 34, 0, Math.PI * 2); x.fill();
  x.fillStyle = '#F4FBE0';
  for (let k = 0; k < 9; k++) { const a = k / 9 * Math.PI * 2; x.beginPath(); x.ellipse(64 + Math.cos(a) * 20, 64 + Math.sin(a) * 20, 5, 2.6, a, 0, Math.PI * 2); x.fill(); }
  const t = new THREE.CanvasTexture(c);
  return [new THREE.MeshStandardMaterial({ color: 0x2E6B2E, roughness: 0.6 }), new THREE.MeshStandardMaterial({ map: t, roughness: 0.5 }), new THREE.MeshStandardMaterial({ map: t, roughness: 0.5 })];
})();

/** 두 눈 위 오이 조각. */
export function makeCukes(): THREE.Group {
  const g = new THREE.Group();
  [1, -1].forEach((sd) => {
    const eye = new THREE.Group(); eye.position.set(sd * 0.51, 0.29, 0.6); eye.rotation.y = sd * Math.atan2(0.53, 0.62); eye.rotation.x = -0.35; g.add(eye);
    const sl = new THREE.Mesh(new THREE.CylinderGeometry(0.17, 0.17, 0.035, 26), CUKE_MATS); sl.rotation.x = Math.PI / 2; sl.position.z = 0.05; eye.add(sl);
  });
  return g;
}

// 팩: 얼굴 전체에 살짝 울퉁불퉁하게 바른 두꺼운 층. 눈·코·입은 셰이더에서 구멍을 낸다.
let maskGeoCache: THREE.BufferGeometry | null = null;
function maskGeo(): THREE.BufferGeometry {
  if (maskGeoCache) return maskGeoCache;
  const lump = (x: number, y: number, z: number) => 0.5 + 0.25 * Math.sin(x * 23 + y * 7) + 0.25 * Math.sin(y * 19 - z * 11);
  function layer(geo: THREE.BufferGeometry, setup: (m: THREE.Mesh) => void, keep: (c: THREE.Vector3) => boolean, base: number, amp: number) {
    const m = new THREE.Mesh(geo); setup(m); m.updateMatrixWorld(true);
    const g = geo.clone().applyMatrix4(m.matrixWorld), p = g.attributes.position!, n = g.attributes.normal!;
    for (let i = 0; i < p.count; i++) {
      const x = p.getX(i), y = p.getY(i), z = p.getZ(i), k = base + amp * lump(x, y, z);
      p.setXYZ(i, x + n.getX(i) * k, y + n.getY(i) * k, z + n.getZ(i) * k);
    }
    const idx = g.index!.array, kept: number[] = [], c = new THREE.Vector3(), v = new THREE.Vector3();
    for (let t = 0; t < idx.length; t += 3) {
      c.set(0, 0, 0);
      for (let k = 0; k < 3; k++) c.add(v.fromBufferAttribute(p as THREE.BufferAttribute, idx[t + k]!));
      c.multiplyScalar(1 / 3);
      if (keep(c)) kept.push(idx[t]!, idx[t + 1]!, idx[t + 2]!);
    }
    g.setIndex(kept); g.computeVertexNormals();
    return new THREE.Mesh(g);
  }
  const hiHead = new THREE.SphereGeometry(0.82, 96, 64), hp = hiHead.attributes.position!;
  for (let i = 0; i < hp.count; i++) { let x = hp.getX(i), y = hp.getY(i), z = hp.getZ(i); const k = y / 0.82; x *= 1 - 0.16 * k; z *= 1 - 0.05 * k; if (y > 0.56) y = 0.56 + (y - 0.56) * 0.55; hp.setXYZ(i, x, y, z); }
  hiHead.computeVertexNormals();
  const hiMuzzle = new THREE.SphereGeometry(0.5, 72, 48), mp = hiMuzzle.attributes.position!;
  for (let i = 0; i < mp.count; i++) mp.setX(i, mp.getX(i) * (1 - 0.14 * mp.getY(i) / 0.5));
  hiMuzzle.computeVertexNormals();
  const a = layer(hiHead, (m) => m.scale.set(0.98, 0.9, 1.18), (c) => c.z > -0.02 && c.y > -0.62 && c.y < 0.64, 0.016, 0.014);
  const b = layer(hiMuzzle, (m) => { m.scale.set(0.98, 0.84, 0.72); m.position.set(0, -0.14, 0.74); }, (c) => c.z > 0.42, 0.016, 0.012);
  maskGeoCache = mergeGeo([a, b]);
  hiHead.dispose(); hiMuzzle.dispose();
  return maskGeoCache;
}

function makeMaskMat(color: THREE.ColorRepresentation): THREE.MeshStandardMaterial {
  const maskMat = new THREE.MeshStandardMaterial({ color, roughness: 0.95, metalness: 0 });
  maskMat.customProgramCacheKey = () => 'clay-mask';
  maskMat.onBeforeCompile = (sh) => {
    sh.vertexShader = 'varying vec3 vMaskP;\n' + sh.vertexShader.replace('#include <begin_vertex>', '#include <begin_vertex>\n vMaskP = position;');
    sh.fragmentShader = 'varying vec3 vMaskP;\n' + sh.fragmentShader.replace('#include <clipping_planes_fragment>', `#include <clipping_planes_fragment>
      float edgeZ = 0.1 + 0.035*sin(vMaskP.x*13.0 + 1.3) + 0.03*sin(vMaskP.y*17.0);
      if (vMaskP.z < edgeZ) discard;
      if (vMaskP.y > 0.5 + 0.035*sin(vMaskP.x*11.0 + 0.6)) discard;
      if (vMaskP.y < -0.55 && vMaskP.z < 0.85) discard;
      if (distance(vMaskP, vec3(0.51, 0.29, 0.6)) < 0.2 || distance(vMaskP, vec3(-0.51, 0.29, 0.6)) < 0.2) discard;
      if (distance(vMaskP, vec3(0.12, 0.1, 1.03)) < 0.065 || distance(vMaskP, vec3(-0.12, 0.1, 1.03)) < 0.065) discard;
      vec2 mo = (vMaskP.xy - vec2(0.0, -0.22)) / vec2(0.085, 0.075);
      if (dot(mo, mo) < 1.0 && vMaskP.z > 0.85) discard;`);
  };
  return maskMat;
}
/** 라군 팩 바의 팩 3종. 씻으면 지워진다. */
export const LAGOON_MASKS: Record<LagoonMaskId, THREE.MeshStandardMaterial> = {
  silica: makeMaskMat(0xEEF2EE), algae: makeMaskMat(0x7FA866), lavaclay: makeMaskMat(0x48423E),
};
const KBEAUTY_MAT = makeMaskMat(0xFBFAF6);
KBEAUTY_MAT.roughness = 0.28; KBEAUTY_MAT.transparent = true; KBEAUTY_MAT.opacity = 0.93;

export function makeMask(kind: 'kbeauty' | LagoonMaskId): THREE.Mesh {
  return new THREE.Mesh(maskGeo(), kind === 'kbeauty' ? KBEAUTY_MAT : LAGOON_MASKS[kind]);
}

const bubbleMat = new THREE.MeshStandardMaterial({ color: 0xDDF4FF, emissive: 0x6FA9C8, emissiveIntensity: 0.3, roughness: 0.05, metalness: 0.1, transparent: true, opacity: 0.34, depthWrite: false });
const bubbleShine = new THREE.MeshBasicMaterial({ color: 0xFFFFFF, transparent: true, opacity: 0.85, depthWrite: false });

/** 콧구멍에 붙는 풍선. 크기는 pose.ts가 숨에 맞춰 바꾼다. */
export function makeBubble(): THREE.Group {
  const g = new THREE.Group(); g.position.set(0.12, 0.08, 1.03);
  const b = new THREE.Mesh(new THREE.SphereGeometry(1, 20, 14), bubbleMat); b.position.set(0, -0.15, 0.95); g.add(b);
  const sh = new THREE.Mesh(new THREE.SphereGeometry(0.2, 10, 8), bubbleShine); sh.position.set(-0.32, 0.25, 1.6); g.add(sh);
  g.scale.setScalar(0.08);
  return g;
}
